import { ChainID } from '@jdiyield/constants'
import { APR, Balance, BalanceLP, PriceList, Token } from '@jdiyield/interfaces'
import axios from 'axios'
import BigNumber from 'bignumber.js'
import Web3 from 'web3'

import * as ABI from '../abi/abi'
import balanceAbi from '../abi/balance.json'
import { calculate } from '../calculate'
import { StakeFunc, StakeProps } from '../types'
import { makeBatchRequest, safeGasEstimates } from '../utils'

type APYBreakdown = {
  vaultApr: number
  compoundingsPerYear: number
  beefyPerformanceFee: number
  vaultApy: number
  lpFee: number
  tradingApr: number
  totalApy: number
}

export const getBeefy = async (
  web3: Web3,
  walletAddress: string,
  routerContractAddress: string,
  tokens: Token[],
  priceList: PriceList,
  chainID: ChainID
): Promise<(Balance | BalanceLP)[]> => {
  try {
    const pools = (await getPoolBeefy(chainID)) || []

    const lpsBalanceOf = await makeBatchRequest(
      web3,
      pools?.map((myPool) => {
        const token = new web3.eth.Contract(balanceAbi as any, myPool.earnedTokenAddress)
        return token.methods.balanceOf(walletAddress).call
      })
    )

    const stakingPools = pools
      .map((pool, index) => ({ ...pool, index }))
      .filter((_, i) => +lpsBalanceOf?.[i] > 0) as any[]
    const _balanceOf = stakingPools?.map((myPool) => {
      return {
        id: myPool.id,
        balanceOf: lpsBalanceOf?.[myPool.index],
        earnedTokenAddress: myPool.earnedTokenAddress,
        tokenAddress: myPool.tokenAddress
      }
    })
    const pricePerFullShares = await makeBatchRequest(
      web3,
      _balanceOf.map(({ earnedTokenAddress }) => {
        const vault = new web3.eth.Contract(ABI.vaultABI as any, earnedTokenAddress)
        return vault.methods.getPricePerFullShare().call
      })
    )

    const _stakingData = _balanceOf.map((ctx, index) => {
      const pricePerFullShare = pricePerFullShares[index]

      return {
        ...ctx,
        balance: ctx.balanceOf,
        pricePerFullShare
      }
    })

    const _balanceLPPair = _stakingData.map(async (i: any) => {
      const balance = new BigNumber(i.balance).multipliedBy(i.pricePerFullShare).dividedBy('1e18').toFixed(0)
      const lp: any = {
        pool: i.id,
        balance: balance,
        lpAddress: i.tokenAddress
      }

      const result = await calculate(web3, lp, routerContractAddress, tokens, priceList)
      return result
    })

    const completeBalance = await Promise.all(_balanceLPPair)

    const complete = completeBalance.map((i) => {
      return {
        ...i,
        availableBalanceOf: '0',

        reward: 0,
        rewardSymbol: ''
      }
    })
    return complete
  } catch (e) {
    return []
  }
}

export const getPoolBeefy = async (chainID: ChainID) => {
  const chain = chainID === 137 ? 'polygon' : 'bsc'
  const poolsResponse = await axios.get(
    `https://raw.githubusercontent.com/beefyfinance/beefy-app/master/src/features/configure/vault/${chain}_pools.js`
  )
  const pools = Object.freeze(
    eval(
      poolsResponse.data.replace(
        chainID === 137 ? /export\s+const\s+polygonPools\s+=\s+/ : /export\s+const\s+bscPools\s+=\s+/,
        ''
      )
    ).filter((p) => {
      return p.status === 'active' || p.depositsPaused !== false
    })
  )
  return pools
}

const findFarmByID = async (pool: number, chainID: ChainID): Promise<any> => {
  const poolFarms = await getPoolBeefy(chainID)
  const filter = poolFarms?.find((i) => i.id === pool)
  return filter
}

export const stakeBeefy: StakeFunc = async ({ web3, pool, walletAddress, amount }) => {
  try {
    const chainID = await web3.eth.getChainId()
    const farm = await findFarmByID(pool, chainID as ChainID)
    const ct = new web3.eth.Contract(ABI.vaultABI as any, farm.earnedTokenAddress)
    const contractWithMethod = ct.methods.deposit(new BigNumber(amount).times(new BigNumber(10).pow(18)).toString())
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })
  } catch (e) {
    return false
  }
}

export const unStakeBeefy: StakeFunc = async ({ web3, pool, walletAddress, amount }) => {
  try {
    const chainID = await web3.eth.getChainId()
    const farm = await findFarmByID(pool, chainID as ChainID)
    const ct = new web3.eth.Contract(ABI.vaultABI as any, farm.earnedTokenAddress)
    const contractWithMethod = ct.methods.withdraw(new BigNumber(amount).times(new BigNumber(10).pow(18)).toString())
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (e) {
    return false
  }
}

export const getRewardBeefy: StakeFunc<Omit<StakeProps, 'amount'>> = async ({ pool, walletAddress, web3 }) => {
  try {
    const chainID = await web3.eth.getChainId()
    const farm = await findFarmByID(pool, chainID as ChainID)
    const ct = new web3.eth.Contract(ABI.vaultABI as any, farm.earnedTokenAddress)
    const contractWithMethod = ct.methods.earn()
    const gasLimit = await safeGasEstimates({ contractWithMethod, walletAddress })

    await contractWithMethod.send({ from: walletAddress, gas: gasLimit })

    return true
  } catch (e) {
    return false
  }
}

export const getBeefyAPR = async (lpId: string): Promise<APR> => {
  try {
    const [apyBreakdown, lpPrice] = await Promise.all([
      axios.get('https://api.beefy.finance/apy/breakdown'),
      axios.get('https://api.beefy.finance/lps')
    ])

    const apyData = apyBreakdown.data[lpId] as APYBreakdown
    const rewardPrice = lpPrice.data[lpId] || 0
    const yearlyAPR = (apyData.vaultApr || apyData.vaultApy || apyData.totalApy) * 100
    const yearAPY = (apyData.vaultApy || apyData.totalApy) * 100
    const dailyAPR = yearlyAPR / 365

    return {
      dailyAPR: yearlyAPR / 365,
      weeklyAPR: dailyAPR * 7,
      monthlyAPR: dailyAPR * 30,
      yearlyAPR: dailyAPR * 365,
      yearAPY,
      rewardPrice
    }
  } catch (err) {
    console.log(lpId, err)
    return null
  }
}
